import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  Row,
  Col,
  Button,
  Form,
  Card,
  Badge,
  Alert,
  ButtonGroup,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faSave,
  faCheckCircle,
  faEye,
  faCode,
  faCopy,
  faDownload,
  faFileExport,
} from '@fortawesome/free-solid-svg-icons';

import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import useDbqForm from '../../hooks/useDbqForm';
import { DbqFormView, dbqStyles } from './dbq';

const DbqFormRenderer = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [viewMode, setViewMode] = useState('ui'); // 'ui' or 'json'
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [editedJson, setEditedJson] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Use shared form hook
  const formContext = useDbqForm();
  const { initializeRepeatingInstances, buildRenderWithGroupMap, setFormJson } = formContext;

  // Fetch the form
  const { data, isLoading, error } = useQuery({
    queryKey: ['dbqForm', formId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/forms/${formId}`);
      return response.data;
    },
  });

  const form = data?.form;
  const formJson = form?.formJson;

  // Build render with group map
  const renderWithGroupMap = useMemo(
    () => (formJson ? buildRenderWithGroupMap(formJson) : {}),
    [formJson, buildRenderWithGroupMap]
  );

  // Initialize repeating instances and set formJson in hook on form load
  useEffect(() => {
    if (formJson) {
      initializeRepeatingInstances(formJson);
      setFormJson(formJson);
    }
  }, [formJson, initializeRepeatingInstances, setFormJson]);

  const handleSave = () => {
    // This is a dead form - just show success message
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleCopyJson = () => {
    if (formJson) {
      navigator.clipboard.writeText(JSON.stringify(formJson, null, 2));
      alert('JSON copied to clipboard!');
    }
  };

  const handleDownloadJson = () => {
    if (formJson) {
      const blob = new Blob([JSON.stringify(formJson, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formJson.id || 'dbq-form'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleEditJson = () => {
    setEditedJson(JSON.stringify(formJson, null, 2));
    setIsEditingJson(true);
    setJsonError(null);
  };

  const handleCancelEdit = () => {
    setIsEditingJson(false);
    setEditedJson('');
    setJsonError(null);
  };

  const handleJsonChange = (e) => {
    const value = e.target.value;
    setEditedJson(value);
    
    // Try to parse JSON to validate it
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (err) {
      setJsonError(err.message);
    }
  };

  const handleSaveJson = async () => {
    try {
      // Validate JSON
      let parsedJson;
      try {
        parsedJson = JSON.parse(editedJson);
      } catch (err) {
        setJsonError(`Invalid JSON: ${err.message}`);
        return;
      }

      setIsSaving(true);
      
      // Save to backend
      const response = await axios.put(`/dbq/forms/${formId}`, {
        formJson: parsedJson,
      });

      if (response.data) {
        setShowSaveSuccess(true);
        setIsEditingJson(false);
        setJsonError(null);
        
        // Refetch the form to get the updated data
        window.location.reload(); // Simple way to refresh the form data
        
        setTimeout(() => setShowSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving JSON:', err);
      setJsonError(
        err.response?.data?.msg || 
        err.response?.data?.errors?.[0]?.message ||
        'Failed to save JSON. Please check your changes.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Generate all possible field IDs for DocuSeal template mapping
  const generateDocuSealFieldIds = () => {
    if (!formJson) return [];

    const fieldIds = [];

    const processField = (field, repeatPrefix = '', repeatIndex = null) => {
      if (!field || !field.id) return;

      // Generate the field ID with repeat prefix if applicable
      const fieldId = repeatPrefix ? `${field.id}_${repeatIndex}` : field.id;

      // Map our field types to DocuSeal types
      const getDocuSealType = (fieldType) => {
        const typeMap = {
          'short_text': 'text',
          'long_text': 'text',
          'number': 'number',
          'date': 'date',
          'yes_no': 'radio',
          'single_select': 'radio',
          'multi_select': 'checkbox',
          'checkbox': 'checkbox',
        };
        return typeMap[fieldType] || 'text';
      };

      const docuSealType = getDocuSealType(field.field_type);

      // For radio buttons (yes_no and single_select), create an entry for each option
      // All options share the same ID to form a radio group
      if (field.field_type === 'yes_no') {
        // Yes/No radio group
        fieldIds.push({
          id: fieldId,
          originalId: field.id,
          label: field.label,
          optionValue: 'Yes',
          type: 'radio',
          code: field.code,
          isRepeated: !!repeatPrefix,
          repeatIndex: repeatIndex,
          description: field.description,
          note: 'Radio group - same ID for all options',
        });
        fieldIds.push({
          id: fieldId,
          originalId: field.id,
          label: field.label,
          optionValue: 'No',
          type: 'radio',
          code: field.code,
          isRepeated: !!repeatPrefix,
          repeatIndex: repeatIndex,
          description: field.description,
          note: 'Radio group - same ID for all options',
        });
      } else if (field.field_type === 'single_select' && field.options) {
        // Single select radio group - one entry per option, all with same ID
        field.options.forEach((option) => {
          fieldIds.push({
            id: fieldId,
            originalId: field.id,
            label: field.label,
            optionValue: option.label,
            type: 'radio',
            code: field.code,
            isRepeated: !!repeatPrefix,
            repeatIndex: repeatIndex,
            description: field.description,
            note: 'Radio group - same ID for all options',
          });

          // Process child elements of this option
          if (option.child_elements) {
            option.child_elements.forEach((childElement) => {
              processElement(childElement, repeatPrefix, repeatIndex);
            });
          }
        });
      } else if (field.field_type === 'multi_select' && field.options) {
        // Multi-select checkboxes - each option gets a unique ID
        field.options.forEach((option) => {
          fieldIds.push({
            id: `${fieldId}.${option.id}`,
            originalId: field.id,
            label: field.label,
            optionValue: option.label,
            type: 'checkbox',
            code: field.code,
            isRepeated: !!repeatPrefix,
            repeatIndex: repeatIndex,
            description: field.description,
            note: 'Checkbox - unique ID per option',
          });

          // Process child elements of this option
          if (option.child_elements) {
            option.child_elements.forEach((childElement) => {
              processElement(childElement, repeatPrefix, repeatIndex);
            });
          }
        });
      } else {
        // All other field types (text, date, number, long_text, etc.)
        fieldIds.push({
          id: fieldId,
          originalId: field.id,
          label: field.label,
          optionValue: null,
          type: docuSealType,
          code: field.code,
          isRepeated: !!repeatPrefix,
          repeatIndex: repeatIndex,
          description: field.description,
          note: null,
        });

        // Process options with child_elements (edge case)
        if (field.options) {
          field.options.forEach((option) => {
            if (option.child_elements) {
              option.child_elements.forEach((childElement) => {
                processElement(childElement, repeatPrefix, repeatIndex);
              });
            }
          });
        }
      }
    };

    const processElement = (element, repeatPrefix = '', repeatIndex = null) => {
      if (!element || !element.type) return;

      if (element.type === 'field') {
        processField(element, repeatPrefix, repeatIndex);
      } else if (element.type === 'group') {
        // Handle repeating groups
        if (element.repeating) {
          const maxItems = parseInt(element.max_items) || 10; // Default to 10 if unlimited (0)
          const actualMax = maxItems === 0 ? 10 : maxItems;

          // Generate IDs for each possible instance
          for (let i = 1; i <= actualMax; i++) {
            if (element.elements) {
              element.elements.forEach((child) => {
                processElement(child, element.id, i);
              });
            }
          }
        } else {
          // Non-repeating group
          if (element.elements) {
            element.elements.forEach((child) => {
              processElement(child, repeatPrefix, repeatIndex);
            });
          }
        }
      }
      // Static text doesn't have input fields, so we skip it
    };

    // Process all sections
    formJson.sections?.forEach((section) => {
      if (section.elements) {
        section.elements.forEach((element) => {
          processElement(element);
        });
      }
    });

    return fieldIds;
  };

  const handleExportDocuSealMapping = () => {
    const fieldIds = generateDocuSealFieldIds();
    
    // Create CSV content
    const csvHeader = 'DocuSeal Field ID,Original ID,Label,Option Value,DocuSeal Type,Code,Is Repeated,Repeat Index,Description,Note\n';
    const csvRows = fieldIds.map((field) => {
      return [
        field.id,
        field.originalId,
        `"${(field.label || '').replace(/"/g, '""')}"`,
        field.optionValue ? `"${field.optionValue.replace(/"/g, '""')}"` : '',
        field.type || '',
        field.code || '',
        field.isRepeated ? 'Yes' : 'No',
        field.repeatIndex || '',
        `"${(field.description || '').replace(/"/g, '""')}"`,
        field.note ? `"${field.note.replace(/"/g, '""')}"` : '',
      ].join(',');
    });
    const csvContent = csvHeader + csvRows.join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formJson.id || 'dbq-form'}_docuseal_mapping.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyDocuSealMapping = () => {
    const fieldIds = generateDocuSealFieldIds();
    const jsonContent = JSON.stringify(fieldIds, null, 2);
    navigator.clipboard.writeText(jsonContent);
    alert(`Copied ${fieldIds.length} field IDs to clipboard!`);
  };

  if (isLoading) {
    return (
      <PrivateLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
            <p className="text-muted">Loading form...</p>
          </div>
        </div>
      </PrivateLayout>
    );
  }

  if (error) {
    return (
      <PrivateLayout>
        <Panel>
          <Alert variant="danger">
            <strong>Error:</strong> Failed to load form.{' '}
            {error.response?.data?.msg || error.message}
          </Alert>
          <Button variant="secondary" onClick={() => navigate('/dbq')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Forms
          </Button>
        </Panel>
      </PrivateLayout>
    );
  }

  return (
    <PrivateLayout>
      <style>{dbqStyles}</style>
      
      <div className="dbq-renderer">
        <Panel className="dbq-panel mx-auto" style={{ maxWidth: '1200px' }}>
          {/* Header */}
          <Row className="mb-5 align-items-center dbq-header">
            <Col xs={12} lg={7}>
              <Button
                variant="link"
                className="p-0 mb-3 text-decoration-none text-muted d-flex align-items-center"
                onClick={() => navigate('/dbq')}
                style={{ fontSize: '0.9rem' }}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to DBQ Library
              </Button>
              <h2 className="mb-2 display-6">{formJson?.title}</h2>
              <div className="d-flex align-items-center flex-wrap gap-3 mt-3">
                {formJson?.version && (
                  <Badge bg="secondary" className="px-3 py-2 fw-normal">
                    v{formJson.version}
                  </Badge>
                )}
                {formJson?.short_name && (
                  <span className="text-muted d-flex align-items-center">
                    <span className="me-2">•</span>
                    {formJson.short_name}
                  </span>
                )}
              </div>
              {formJson?.description && (
                <p className="mt-3 text-muted lead" style={{ fontSize: '1.1rem' }}>
                  {formJson.description}
                </p>
              )}
            </Col>
            <Col xs={12} lg={5} className="text-lg-end mt-4 mt-lg-0">
              {/* DocuSeal Mapping Export */}
              <div className="mb-3">
                <ButtonGroup size="sm">
                  <Button
                    variant="outline-info"
                    onClick={handleExportDocuSealMapping}
                    className="d-flex align-items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faFileExport} />
                    Export DocuSeal Mapping (CSV)
                  </Button>
                  <Button
                    variant="outline-info"
                    onClick={handleCopyDocuSealMapping}
                    className="d-flex align-items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                    Copy as JSON
                  </Button>
                </ButtonGroup>
              </div>

              {/* View Toggle */}
              <div className="dbq-toggle d-inline-flex bg-light rounded-pill p-1 border border-secondary border-opacity-25">
                <Button
                  variant={viewMode === 'ui' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('ui')}
                  size="sm"
                >
                  <FontAwesomeIcon icon={faEye} className="me-2" />
                  Form UI
                </Button>
                <Button
                  variant={viewMode === 'json' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('json')}
                  size="sm"
                >
                  <FontAwesomeIcon icon={faCode} className="me-2" />
                  JSON Schema
                </Button>
              </div>
              {showSaveSuccess && viewMode === 'ui' && (
                <div className="text-success mt-3 animate-fade-in fw-medium">
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                  Form saved successfully!
                </div>
              )}
            </Col>
          </Row>

          {/* UI View */}
          {viewMode === 'ui' && (
            <Form>
              <DbqFormView
                formJson={formJson}
                formContext={formContext}
                renderWithGroupMap={renderWithGroupMap}
              />

              {/* Save Button */}
              <div className="dbq-save-zone">
                <Button
                  className="dbq-save-btn"
                  size="lg"
                  onClick={handleSave}
                >
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  Save Form Progress
                </Button>
                <div className="text-muted small mt-3 opacity-75">
                  This is a preview mode - changes are simulated
                </div>
              </div>
            </Form>
          )}

          {/* JSON View */}
          {viewMode === 'json' && (
            <div className="animate-fade-in">
              <Row className="mb-3">
                <Col xs={12}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Form JSON Schema</h5>
                    <div>
                      {!isEditingJson ? (
                        <>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={handleCopyJson}
                          >
                            <FontAwesomeIcon icon={faCopy} className="me-1" />
                            Copy
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={handleDownloadJson}
                          >
                            <FontAwesomeIcon icon={faDownload} className="me-1" />
                            Download
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleEditJson}
                          >
                            <FontAwesomeIcon icon={faCode} className="me-1" />
                            Edit JSON
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={handleSaveJson}
                            disabled={!!jsonError || isSaving}
                          >
                            {isSaving ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faSave} className="me-1" />
                                Save Changes
                              </>
                            )}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {showSaveSuccess && (
                    <Alert variant="success" className="animate-fade-in">
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      JSON saved successfully!
                    </Alert>
                  )}
                  {jsonError && (
                    <Alert variant="danger" className="animate-fade-in">
                      <strong>Error:</strong> {jsonError}
                    </Alert>
                  )}
                </Col>
              </Row>

              {/* Form Metadata */}
              <Card className="mb-4 dbq-json-card">
                <Card.Body>
                  <h6 className="mb-3">Form Metadata</h6>
                  <Row className="g-3">
                    <Col xs={12} sm={6} md={3}>
                      <small className="text-muted d-block mb-1">Form ID</small>
                      <div className="font-monospace">
                        {formJson.id}
                      </div>
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <small className="text-muted d-block mb-1">Title</small>
                      <div>
                        {formJson.title}
                      </div>
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <small className="text-muted d-block mb-1">Sections</small>
                      <div>
                        {formJson.sections?.length || 0}
                      </div>
                    </Col>
                    <Col xs={12} sm={6} md={3}>
                      <small className="text-muted d-block mb-1">Version</small>
                      <div>
                        {formJson.version || 'N/A'}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* JSON Display/Editor */}
              <Card className="dbq-json-card">
                <Card.Body className="p-0">
                  {isEditingJson ? (
                    <Form.Control
                      as="textarea"
                      value={editedJson}
                      onChange={handleJsonChange}
                      style={{
                        backgroundColor: '#f8f9fa',
                        color: '#1a1a1a',
                        border: 'none',
                        padding: '1.5rem',
                        minHeight: '70vh',
                        fontSize: '0.85rem',
                        fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                        lineHeight: '1.5',
                        resize: 'vertical',
                      }}
                      className={jsonError ? 'border-danger' : ''}
                    />
                  ) : (
                    <pre
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '1.5rem',
                        margin: 0,
                        maxHeight: '70vh',
                        overflow: 'auto',
                        fontSize: '0.85rem',
                        fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                        lineHeight: '1.5',
                        color: '#1a1a1a',
                      }}
                    >
                      <code>{JSON.stringify(formJson, null, 2)}</code>
                    </pre>
                  )}
                </Card.Body>
              </Card>

              {/* Processing Metadata */}
              {form?.metadata && (
                <Card className="mt-4 dbq-json-card">
                  <Card.Body>
                    <h6 className="mb-3">AI Processing Details</h6>
                    <Row>
                      <Col xs={12} sm={6}>
                        <small className="text-muted d-block mb-1">Model</small>
                        <div className="font-monospace">
                          {form.metadata.model}
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <small className="text-muted d-block mb-1">Tokens Used</small>
                        <div>
                          {form.metadata.tokensUsed?.total_tokens || 'N/A'}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </div>
          )}
        </Panel>
      </div>
    </PrivateLayout>
  );
};

export default DbqFormRenderer;
